import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import jobService from '../../services/jobService';
import consultantApi from '../../services/consultantApi';
import api from '../../services/api';
import { validateJobForm, cleanJobFormData, getJobErrorMessage } from '../../utils/jobValidation';
import './EditJob.css';

const EditJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingJob, setLoadingJob] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [educations, setEducations] = useState([]);
  const [employerSkills, setEmployerSkills] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [benefits, setBenefits] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    responsibilities: '',
    requirements: '',
    benefits: [],
    customBenefits: [], // For custom text benefits
    jobType: '',
    shiftType: '',
    industryType: '',
    educationRequired: '',
    applicationMethod: {
      type: 'internal'
    },
    workMode: 'onsite',
    location: {
      country: '',
      state: '',
      city: '',
      address: ''
    },
    salary: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'monthly',
      negotiable: false
    },
    experienceRequired: {
      min: 0,
      max: 0
    },
    requiredSkills: [],
    optionalSkills: [],
    numberOfPositions: 0,
    isFeatured: false,
    isUrgent: false,
    status: 'draft'
  });

  const [customBenefitInput, setCustomBenefitInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchMasterData();
    fetchJobData();
  }, [id]);

  const fetchJobData = async () => {
    try {
      setLoadingJob(true);
      const response = await jobService.getJobById(id);
      const job = response.data;

      let formData = {
        title: job.title || '',
        company: job.companyId?._id || '',
        companyId: job.companyId?._id || '',
        description: job.description || '',
        responsibilities: job.responsibilities || '',
        requirements: job.requirements || '',
        benefits: job.benefits?.map(b => b._id || b) || [],
        customBenefits: job.customBenefits || [],
        jobType: job.jobType?._id || '',
        shiftType: job.shiftType?._id || '',
        industryType: job.industryType?._id || '',
        educationRequired: job.educationRequired?._id || '',
        applicationMethod: job.applicationMethod || { type: 'internal' },
        workMode: job.workMode || 'onsite',
        location: {
          country: job.location?.country || '',
          state: job.location?.state || '',
          city: job.location?.city?.name || job.location?.city || '',
          address: job.location?.address || '',
          isRemote: job.location?.isRemote || false
        },
        salary: {
          min: job.salary?.min || 0,
          max: job.salary?.max || 0,
          currency: job.salary?.currency || 'INR',
          period: job.salary?.period || 'monthly',
          negotiable: job.salary?.negotiable || false
        },
        experienceRequired: {
          min: job.experienceRequired?.min || 0,
          max: job.experienceRequired?.max || 0
        },
        requiredSkills: job.requiredSkills?.map(s => s._id || s) || [],
        optionalSkills: job.optionalSkills?.map(s => s._id || s) || [],
        numberOfPositions: job.numberOfPositions || 0,
        isFeatured: job.isFeatured || false,
        isUrgent: job.isUrgent || false,
        status: job.status || 'draft'
      }

      setFormData(formData);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job data');
      navigate('/employer/my-jobs');
    } finally {
      setLoadingJob(false);
    }
  };

  const fetchMasterData = async () => {
    try {
      let companiesPromise;
      if (user?.role === 'consultant') {
        companiesPromise = consultantApi.getMyCompanies({ status: 'accepted' });
      } else {
        companiesPromise = api.get('/employer/company/my-companies');
      }

      const [
        companiesRes,
        jobTypesRes,
        shiftTypesRes,
        industryTypesRes,
        educationsRes,
        employerSkillsRes
      ] = await Promise.all([
        companiesPromise,
        api.get('/master/job-types'),
        api.get('/master/shift-types'),
        api.get('/master/industry-types'),
        api.get('/master/educations'),
        api.get('/employer/skills').catch(() => ({ data: [] }))
      ]);

      setCompanies(companiesRes.data || []);
      setJobTypes(jobTypesRes.data || []);
      setShiftTypes(shiftTypesRes.data || []);
      setIndustryTypes(industryTypesRes.data || []);
      setEducations(educationsRes.data || []);
      setEmployerSkills(employerSkillsRes.data || []);

      // Benefits: try employer benefits (admin + own), fallback to master, then hardcoded
      let allBenefits = [];
      try {
        const employerBenefitsRes = await api.get('/employer/benefits');
        allBenefits = employerBenefitsRes.data || [];
      } catch (_) {}
      try {
        const masterBenefitsRes = await api.get('/master/benefits');
        const masterBenefits = masterBenefitsRes.data || [];
        masterBenefits.forEach(b => {
          if (!allBenefits.find(x => x._id === b._id)) allBenefits.push(b);
        });
      } catch (_) {}
      if (allBenefits.length > 0) {
        setBenefits(allBenefits);
      } else {
        setBenefits([
          { _id: '507f1f77bcf86cd799439011', name: 'Health Insurance' },
          { _id: '507f1f77bcf86cd799439012', name: 'Dental Insurance' },
          { _id: '507f1f77bcf86cd799439013', name: 'Vision Insurance' },
          { _id: '507f1f77bcf86cd799439014', name: '401(k) Retirement Plan' },
          { _id: '507f1f77bcf86cd799439015', name: 'Paid Time Off (PTO)' },
          { _id: '507f1f77bcf86cd799439016', name: 'Sick Leave' },
          { _id: '507f1f77bcf86cd799439017', name: 'Maternity/Paternity Leave' },
          { _id: '507f1f77bcf86cd799439018', name: 'Flexible Work Hours' },
          { _id: '507f1f77bcf86cd799439019', name: 'Remote Work Options' },
          { _id: '507f1f77bcf86cd79943901a', name: 'Gym Membership' },
          { _id: '507f1f77bcf86cd79943901b', name: 'Training & Development' },
          { _id: '507f1f77bcf86cd79943901c', name: 'Performance Bonuses' },
          { _id: '507f1f77bcf86cd79943901d', name: 'Stock Options' },
          { _id: '507f1f77bcf86cd79943901e', name: 'Commuter Benefits' },
          { _id: '507f1f77bcf86cd79943901f', name: 'Life Insurance' },
          { _id: '507f1f77bcf86cd799439020', name: 'Childcare Support' },
          { _id: '507f1f77bcf86cd799439021', name: 'Wellness Programs' },
          { _id: '507f1f77bcf86cd799439022', name: 'Professional Development' },
          { _id: '507f1f77bcf86cd799439023', name: 'Team Building Events' },
          { _id: '507f1f77bcf86cd799439024', name: 'Free Lunch/Snacks' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching master data:', error);
      toast.error('Failed to load form data');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const sanitized = value.replace(/\D/g, '');
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: sanitized === '' ? '' : Number(sanitized)
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: sanitized === '' ? '' : Number(sanitized)
      }));
    }
  };

  const handleBenefitsChange = (e) => {
    const { value, checked } = e.target;

    setFormData(prev => ({
      ...prev,
      benefits: checked
        ? [...prev.benefits, value]
        : prev.benefits.filter(id => id !== value)
    }));
  };

  const handleAddCustomBenefit = () => {
    const trimmed = customBenefitInput.trim();
    if (trimmed && !formData.customBenefits.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customBenefits: [...prev.customBenefits, trimmed]
      }));
      setCustomBenefitInput('');
    }
  };

  const handleRemoveCustomBenefit = (benefitToRemove) => {
    setFormData(prev => ({
      ...prev,
      customBenefits: prev.customBenefits.filter(b => b !== benefitToRemove)
    }));
  };

  const handleAddEmployerSkill = async () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;

    try {
      const res = await api.post('/employer/skills', {
        name: trimmed
      });

      const createdSkill = res.data;

      setEmployerSkills(prev => [...prev, createdSkill]);

      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, createdSkill._id]
      }));

      setNewSkill('');
      toast.success('Skill added successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || 'Failed to add skill');
    }
  };

  const handleRemoveEmployerSkill = async (skillId) => {
    try {
      await api.delete(`/employer/skills/${skillId}`);
      setEmployerSkills(prev => prev.filter(s => s._id !== skillId));

      setFormData(prev => ({
        ...prev,
        requiredSkills: prev.requiredSkills.filter(id => id !== skillId),
        optionalSkills: prev.optionalSkills?.filter(id => id !== skillId) || []
      }));

      toast.success('Skill deleted successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || 'Failed to delete skill');
    }
  };

  const handleSkillsChange = (e, type) => {
    const { value, checked } = e.target;
    const skillType = type === 'required' ? 'requiredSkills' : 'optionalSkills';

    setFormData(prev => ({
      ...prev,
      [skillType]: checked
        ? [...prev[skillType], value]
        : prev[skillType].filter(id => id !== value)
    }));
  };

  const handleSubmit = async (e, status) => {
    e.preventDefault();

    const data = { ...formData, status: status || formData.status };
    const validation = validateJobForm(data);
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    try {
      setLoading(true);
      const cleanedData = cleanJobFormData(data);
      await jobService.updateJob(id, cleanedData);
      toast.success('Job updated successfully!');
      navigate('/employer/my-jobs');
    } catch (error) {
      console.error('Error updating job:', error);
      const errorMessage = getJobErrorMessage(error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingJob) {
    return (
      <div className="emp-edit-job-container">
        <div className="emp-edit-job-card" style={{ textAlign: 'center' }}>
          <div className="spinner"></div>
          <p>Loading job data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emp-edit-job-container">
      {/* Hero */}
      <div className="emp-edit-job-hero">
        <h1>Edit Job</h1>
        <p>Update job posting details</p>
      </div>

      <form className="emp-edit-job-form">
        {/* Basic Information */}
        <div className="emp-edit-job-card">
          <h2 className="emp-edit-job-card-title">
            <span className="material-symbols-outlined">info</span>
            Basic Information
          </h2>
          <div className="emp-edit-job-form">
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Job Title <span>*</span></label>
                <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="Job Title" className="emp-edit-job-input" required />
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Job Type <span>*</span></label>
                <select name="jobType" value={formData.jobType} onChange={handleChange} className="emp-edit-job-select" required>
                  <option value="">Select Job Type</option>
                  {jobTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}
                </select>
              </div>
            </div>

            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Work Mode <span>*</span></label>
                <select name="workMode" value={formData.workMode} onChange={handleChange} className="emp-edit-job-select" required>
                  <option value="onsite">Onsite</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Shift Type</label>
                <select name="shiftType" value={formData.shiftType} onChange={handleChange} className="emp-edit-job-select">
                  <option value="">Select Shift Type</option>
                  {shiftTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}
                </select>
              </div>
            </div>

            <div className="emp-edit-job-field" style={{maxWidth:200}}>
              <label className="emp-edit-job-label">Number of Positions</label>
              <input type="number" name="numberOfPositions" value={formData.numberOfPositions} onChange={handleChange} min="1" className="emp-edit-job-input" />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="emp-edit-job-card">
          <h2 className="emp-edit-job-card-title">
            <span className="material-symbols-outlined">location_on</span>
            Location
          </h2>
          <div className="emp-edit-job-form">
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Address</label>
                <input type="text" name="location.address" value={formData.location.address} onChange={handleChange} placeholder="Street address" className="emp-edit-job-input" />
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Country</label>
                <input type="text" name="location.country" value={formData.location.country || ''} onChange={handleChange} className="emp-edit-job-input" />
              </div>
            </div>
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">State</label>
                <input type="text" name="location.state" value={formData.location.state || ''} onChange={handleChange} className="emp-edit-job-input" />
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">City</label>
                <input type="text" name="location.city" value={formData.location.city || ''} onChange={handleChange} className="emp-edit-job-input" />
              </div>
            </div>
          </div>
        </div>

        {/* Job Description */}
        <div className="emp-edit-job-card">
          <h2 className="emp-edit-job-card-title">
            <span className="material-symbols-outlined">description</span>
            Job Description
          </h2>
          <div className="emp-edit-job-form">
            <div className="emp-edit-job-field">
              <label className="emp-edit-job-label">Description <span>*</span></label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={5} placeholder="Describe the job role..." className="emp-edit-job-textarea" required />
            </div>
            <div className="emp-edit-job-field">
              <label className="emp-edit-job-label">Additional Requirements</label>
              <textarea name="requirements" value={formData.requirements} onChange={handleChange} rows={5} placeholder="List requirements and qualifications..." className="emp-edit-job-textarea" />
            </div>

            <div className="emp-edit-job-field">
              <label className="emp-edit-job-label">Benefits</label>
              <div className="emp-edit-job-skills-grid">
                {benefits.map(b => (
                  <label key={b._id} className={`emp-edit-job-skill-chip${formData.benefits.includes(b._id) ? ' selected' : ''}`}>
                    <input type="checkbox" value={b._id} checked={formData.benefits.includes(b._id)} onChange={handleBenefitsChange} />
                    {b.name}
                  </label>
                ))}
              </div>

              {formData.customBenefits.length > 0 && (
                <div style={{marginTop: 15}}>
                  <div style={{fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600}}>Custom Benefits:</div>
                  <div className="emp-edit-job-skills-grid">
                    {formData.customBenefits.map((customBenefit, index) => (
                      <label key={`custom-${index}`} className="emp-edit-job-skill-chip selected" style={{backgroundColor: '#e8f5e8', borderColor: '#4caf50'}}>
                        <span>{customBenefit}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomBenefit(customBenefit)}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ×
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="emp-edit-job-benefit-row" style={{marginTop:15}}>
                <input
                  type="text"
                  placeholder="Add custom benefit..."
                  value={customBenefitInput}
                  onChange={(e) => setCustomBenefitInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCustomBenefit()}
                  className="emp-edit-job-input emp-edit-job-benefit-input"
                />
                <button type="button" onClick={handleAddCustomBenefit} className="emp-edit-job-add-btn">
                  <span className="material-symbols-outlined">add</span> Add
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Skills */}
        <div className="emp-edit-job-card">
          <h2 className="emp-edit-job-card-title">
            <span className="material-symbols-outlined">psychology</span>
            Skills
          </h2>
          <div className="emp-edit-job-form">
            <div className="emp-edit-job-field">
              {employerSkills.length > 0 && (
                <div style={{marginBottom: 15}}>
                  <div style={{fontSize: 14, color: '#101721', marginBottom: 8, fontWeight: 500}}>Available Skills:</div>
                  <div className="emp-edit-job-skills-grid">
                    {employerSkills.map(skill => (
                      <label key={skill._id} className={`emp-edit-job-skill-chip${formData.requiredSkills.includes(skill._id) ? ' selected' : ''}`}>
                        <input type="checkbox" value={skill._id} checked={formData.requiredSkills.includes(skill._id)} onChange={(e) => handleSkillsChange(e, 'required')} />
                        <span>{skill.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveEmployerSkill(skill._id)}
                          style={{
                            marginLeft: 8,
                            background: 'none',
                            border: 'none',
                            color: '#f44336',
                            cursor: 'pointer',
                            fontSize: 16,
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          ×
                        </button>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="emp-edit-job-benefit-row" style={{marginBottom: 15}}>
                <input
                  type="text"
                  placeholder="Add custom skill..."
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmployerSkill()}
                  className="emp-edit-job-input emp-edit-job-benefit-input"
                />
                <button type="button" onClick={handleAddEmployerSkill} className="emp-edit-job-add-btn">
                  <span className="material-symbols-outlined">add</span> Add Skill
                </button>
              </div>
            </div>
            <div className="emp-edit-job-field">
              <label className="emp-edit-job-label">Experience <span>*</span></label>
              <div className="emp-edit-job-row">
                <div className="emp-edit-job-field">
                  <label className="emp-edit-job-label">Min</label>
                  <input type="text" name="experienceRequired.min" value={formData.experienceRequired?.min} onChange={handleNumericChange} inputMode="numeric" pattern="[0-9]*" className="emp-edit-job-input" required />
                </div>
                <div className="emp-edit-job-field">
                  <label className="emp-edit-job-label">Max</label>
                  <input type="text" name="experienceRequired.max" value={formData.experienceRequired?.max} onChange={handleNumericChange} inputMode="numeric" pattern="[0-9]*" className="emp-edit-job-input" required />
                </div>
              </div>
            </div>
            <div className="emp-edit-job-field">
              <label className="emp-edit-job-label">Education Details</label>
              <select name="educationRequired" value={formData.educationRequired} onChange={handleChange} className="emp-edit-job-select">
                <option value="">Select Education Level</option>
                {educations.map(edu => <option key={edu._id} value={edu._id}>{edu.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Salary */}
        <div className="emp-edit-job-card">
          <h2 className="emp-edit-job-card-title">
            <span className="material-symbols-outlined">currency_rupee</span>
            Salary
          </h2>
          <div className="emp-edit-job-form">
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Min Salary</label>
                <input type="number" name="salary.min" value={formData.salary.min} onChange={handleChange} min="0" className="emp-edit-job-input" />
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Max Salary</label>
                <input type="number" name="salary.max" value={formData.salary.max} onChange={handleChange} min="0" className="emp-edit-job-input" />
              </div>
            </div>
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Currency</label>
                <select name="salary.currency" value={formData.salary.currency} onChange={handleChange} className="emp-edit-job-select">
                  <option value="INR">INR (₹)</option>
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                </select>
              </div>
              <div className="emp-edit-job-field">
                <label className="emp-edit-job-label">Period</label>
                <select name="salary.period" value={formData.salary.period} onChange={handleChange} className="emp-edit-job-select">
                  <option value="hourly">Per Hour</option>
                  <option value="daily">Per Day</option>
                  <option value="monthly">Per Month</option>
                  <option value="yearly">Per Year</option>
                </select>
              </div>
            </div>
            <div className="emp-edit-job-row">
              <div className="emp-edit-job-field" style={{ justifyContent: 'flex-start' }}>
                <label className="emp-edit-job-checkbox-row">
                  <input type="checkbox" name="salary.negotiable" checked={formData.salary.negotiable} onChange={handleChange} />
                  Mark salary as Negotiable
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="emp-edit-job-form-actions">
          <button type="button" className="emp-edit-job-cancel-btn" onClick={() => navigate('/employer/my-jobs')} disabled={loading}>Cancel</button>
          <button type="button" className="emp-edit-job-draft-btn" onClick={(e) => handleSubmit(e, 'draft')} disabled={loading}>
            <span className="material-symbols-outlined">save</span>
            {loading ? 'Saving…' : 'Save Draft'}
          </button>
          <button type="button" className="emp-edit-job-preview-btn" onClick={() => setShowPreview(true)} disabled={loading}>
            <span className="material-symbols-outlined">visibility</span>
            Preview
          </button>
          <button type="button" className="emp-edit-job-publish-btn" onClick={(e) => handleSubmit(e, 'active')} disabled={loading}>
            <span className="material-symbols-outlined">rocket_launch</span>
            {loading ? 'Publishing…' : 'Publish Job'}
          </button>
        </div>
      </form>

      {showPreview && (
        <div className="emp-edit-job-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="emp-edit-job-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="emp-edit-job-preview-close" onClick={() => setShowPreview(false)}>×</button>

            <div className="emp-edit-job-preview-hero">
              <h2>{formData.title || 'Job Title'}</h2>
              <p>{companies[0]?.name || 'Company'} • {[formData.location.city, formData.location.state, formData.location.country].filter(Boolean).join(', ')}</p>
              <div className="emp-edit-job-preview-meta">
                {formData.workMode && <span><span className="material-symbols-outlined">business_center</span>{formData.workMode}</span>}
              </div>
            </div>

            <div className="emp-edit-job-preview-body">
              <div className="emp-edit-job-preview-summary">
                <div className="emp-edit-job-preview-summary-item"><strong>Salary</strong>{formData.salary.currency} {formData.salary.min?.toLocaleString()} – {formData.salary.max?.toLocaleString()} / {formData.salary.period}{formData.salary.negotiable ? ' (Negotiable)' : ''}</div>
                <div className="emp-edit-job-preview-summary-item"><strong>Experience</strong>{formData.experienceRequired.min} – {formData.experienceRequired.max} Years</div>
                <div className="emp-edit-job-preview-summary-item"><strong>Positions</strong>{formData.numberOfPositions}</div>
                {formData.applicationDeadline && <div className="emp-edit-job-preview-summary-item"><strong>Deadline</strong>{new Date(formData.applicationDeadline).toLocaleDateString(undefined, {day:'numeric',month:'short',year:'numeric'})}</div>}
                {formData.jobType && <div className="emp-edit-job-preview-summary-item"><strong>Job Type</strong>{jobTypes.find(j => j._id === formData.jobType)?.name || 'N/A'}</div>}
                {formData.shiftType && <div className="emp-edit-job-preview-summary-item"><strong>Shift</strong>{shiftTypes.find(s => s._id === formData.shiftType)?.name || 'N/A'}</div>}
                {formData.educationRequired && <div className="emp-edit-job-preview-summary-item"><strong>Education</strong>{educations.find(e => e._id === formData.educationRequired)?.name || 'N/A'}</div>}
              </div>

              {formData.description && <div className="emp-edit-job-preview-section"><h3>Description</h3><p>{formData.description}</p></div>}
              {formData.responsibilities && <div className="emp-edit-job-preview-section"><h3>Responsibilities</h3><p>{formData.responsibilities}</p></div>}
              {formData.requirements && <div className="emp-edit-job-preview-section"><h3>Requirements</h3><p>{formData.requirements}</p></div>}

              {formData.requiredSkills.length > 0 && (
                <div className="emp-edit-job-preview-section">
                  <h3>Required Skills</h3>
                  <div className="emp-edit-job-preview-chips">
                    {formData.requiredSkills.map(id => {
                      const s = employerSkills.find(x => x._id === id);
                      return s ? <span key={id} className="emp-edit-job-preview-chip">{s.name}</span> : null;
                    })}
                  </div>
                </div>
              )}
              {formData.benefits.length > 0 && (
                <div className="emp-edit-job-preview-section">
                  <h3>Benefits</h3>
                  <div className="emp-edit-job-preview-chips">
                    {formData.benefits.map(id => { const b = benefits.find(x => x._id === id); return b ? <span key={id} className="emp-edit-job-preview-chip">{b.name}</span> : null; })}
                  </div>
                </div>
              )}

              <div className="emp-edit-job-preview-section">
                <h3>How to Apply</h3>
                {formData.applicationMethod.type === 'internal' && <p>Apply directly on HiringOpen (Internal Application)</p>}
                {formData.applicationMethod.type === 'email' && <p>Send application to: <strong>{formData.applicationMethod.email || 'Not specified'}</strong></p>}
                {formData.applicationMethod.type === 'external' && <p>Apply via: <a href={formData.applicationMethod.externalUrl} target="_blank" rel="noopener noreferrer">{formData.applicationMethod.externalUrl || 'Not specified'}</a></p>}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EditJob;
